import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { LuPlus, LuReceipt } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import AddPaymentModal from './AddPaymentModal';
import useEmployeeFinanceText from './employeeFinanceText';

// Ödeme türlerinin kayıtlı (Türkçe) değerleri; bilinenler çevrilir
const KNOWN_TYPES = ['Avans', 'Prim', 'Harcırah', 'Yol-Yemek', 'Diğer'];
const STATUS_TONES = { 'ödendi': 'success', odendi: 'success', beklemede: 'warning', bekliyor: 'warning', 'iptal edildi': 'danger', iptal: 'danger' };
const TH = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-text-slate-400';
const TD = 'tw-px-3 tw-py-2.5 tw-text-sm';

/**
 * Payment history of the selected employee: the monthly salary payments
 * (Salaries.payments) and the other payments (Payments), newest first; new
 * payment. Same sources as before.
 */
const PaymentsModal = ({ selectedEmployee, onClose }) => {
  const { language, label, formatMoney, formatDate } = useEmployeeFinanceText();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  const fetchPayments = useCallback(async () => {
    if (!selectedEmployee?.id) return;
    try {
      // Salary payments
      const salaryQuery = query(collection(db, 'Salaries'), where('employeeId', '==', selectedEmployee.id));
      const salarySnapshot = await getDocs(salaryQuery);

      const allPayments = [];
      salarySnapshot.docs.forEach((docSnapshot) => {
        const salaryData = docSnapshot.data();
        if (salaryData.payments) {
          Object.entries(salaryData.payments).forEach(([yearMonth, payment]) => {
            allPayments.push({
              id: `${docSnapshot.id}-${yearMonth}`,
              paymentDate: payment.paidDate,
              paymentType: 'Maaş Ödemesi',
              amount: payment.amount,
              description: `${yearMonth} dönemi maaş ödemesi`,
              status: 'Ödendi',
              isSalary: true,
              period: yearMonth,
            });
          });
        }
      });

      // Other payments
      const paymentsQuery = query(collection(db, 'Payments'), where('employeeId', '==', selectedEmployee.id));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      paymentsSnapshot.docs.forEach((docSnapshot) => {
        const paymentData = docSnapshot.data();
        allPayments.push({
          id: docSnapshot.id,
          paymentDate: paymentData.paymentDate,
          paymentType: paymentData.paymentType || 'Diğer Ödeme',
          amount: paymentData.paidAmount,
          description: paymentData.description,
          status: paymentData.status,
        });
      });

      // Most recent first
      allPayments.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      setPayments(allPayments);
    } catch (error) {
      console.error('Ödeme bilgileri alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const total = useMemo(() => payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0), [payments]);
  const typeText = (payment) => {
    if (payment.isSalary) return label('payments.salaryType');
    if (KNOWN_TYPES.includes(payment.paymentType)) return label(`paymentTypes.${payment.paymentType}`);
    return payment.paymentType === 'Diğer Ödeme' ? label('payments.otherType') : payment.paymentType;
  };
  const descriptionText = (payment) => (payment.isSalary ? label('payments.salaryDescription', { period: payment.period }) : payment.description);
  const statusBadge = (status) => {
    const text = status || label('payments.paid');
    return <Badge tone={STATUS_TONES[String(text).toLocaleLowerCase('tr-TR')] || 'success'}>{text}</Badge>;
  };
  const employeeName = `${selectedEmployee?.eName || ''} ${selectedEmployee?.eSurname || ''}`.trim();

  let body;
  if (loading) {
    body = <p className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('payments.loading')}</p>;
  } else if (payments.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-10 tw-text-center">
        <LuReceipt aria-hidden="true" className="tw-h-8 tw-w-8 tw-text-slate-300 dark:tw-text-slate-600" />
        <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('payments.empty')}</p>
      </div>
    );
  } else {
    body = (
      <>
        <ul className="tw-space-y-2 md:tw-hidden">
          {payments.map((payment) => (
            <li key={payment.id} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                <div className="tw-min-w-0">
                  <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{typeText(payment)}</p>
                  <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{formatDate(payment.paymentDate)}</p>
                </div>
                <p className="tw-shrink-0 tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(payment.amount)}</p>
              </div>
              {descriptionText(payment) && <p className="tw-mt-1.5 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">{descriptionText(payment)}</p>}
              <div className="tw-mt-2">{statusBadge(payment.status)}</div>
            </li>
          ))}
        </ul>
        <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 md:tw-block dark:tw-border-white/10 glass-scrollbar">
          <table className="tw-w-full tw-border-collapse">
            <thead className="tw-bg-slate-50/80 dark:tw-bg-white/[0.03]">
              <tr>
                <th className={TH}>{label('payments.columns.date')}</th>
                <th className={TH}>{label('payments.columns.type')}</th>
                <th className={`${TH} tw-text-right`}>{label('payments.columns.amount')}</th>
                <th className={TH}>{label('payments.columns.description')}</th>
                <th className={TH}>{label('payments.columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="tw-border-t tw-border-slate-200/70 dark:tw-border-white/[0.06]">
                  <td className={`${TD} tw-whitespace-nowrap tw-text-slate-700 dark:tw-text-slate-200`}>{formatDate(payment.paymentDate)}</td>
                  <td className={`${TD} tw-whitespace-nowrap tw-font-medium tw-text-slate-900 dark:tw-text-white`}>{typeText(payment)}</td>
                  <td className={`${TD} tw-whitespace-nowrap tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white`}>{formatMoney(payment.amount)}</td>
                  <td className={`${TD} tw-whitespace-normal tw-text-slate-600 dark:tw-text-slate-300`}>{descriptionText(payment) || '-'}</td>
                  <td className={`${TD} tw-whitespace-nowrap`}>{statusBadge(payment.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="lg"
        title={label('payments.title')}
        subtitle={employeeName}
        closeLabel={label('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
            <LuReceipt aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <Button variant="primary" size="sm" onClick={() => setShowAddPaymentModal(true)}>
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            <span className="tw-hidden sm:tw-inline">{label('payments.add')}</span>
          </Button>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{label('common.close')}</Button>}
      >
        <div className="tw-space-y-3">
          {!loading && payments.length > 0 && (
            <p className="tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-2 tw-rounded-2xl tw-bg-slate-900/[0.03] tw-px-4 tw-py-2.5 tw-text-sm dark:tw-bg-white/[0.04]">
              <span className="tw-text-slate-500 dark:tw-text-slate-400">{label('payments.total', { count: payments.length })}</span>
              <span className="tw-text-base tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(total)}</span>
            </p>
          )}
          {body}
        </div>
      </Modal>

      {showAddPaymentModal && selectedEmployee && (
        <AddPaymentModal
          onClose={() => setShowAddPaymentModal(false)}
          onPaymentAdded={() => {
            fetchPayments();
            setShowAddPaymentModal(false);
          }}
          employeeId={selectedEmployee.id}
          employeeName={employeeName}
        />
      )}
    </>
  );
};

PaymentsModal.propTypes = {
  selectedEmployee: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default PaymentsModal;
